/* eslint-disable no-async-promise-executor */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateStorage } from 'zustand/middleware';
import { encrypted } from '../encrypt-storage';
import { isEmpty } from '../utils';
import { INDEXED_DB_CONFIG } from './indexed-db-config';

export type TOptionsIdb = {
  isDevelopmentEnv: boolean;
  dbName?: string;
  storeName?: string;
  version?: number;
  dbConnection?: IDBDatabase | Promise<IDBDatabase>;
};

let globalDBPromise: Promise<IDBDatabase> | null = null;

function getDB(opt: TOptionsIdb): Promise<IDBDatabase> {
  // If user provided their own active connection
  if (opt.dbConnection) {
    globalDBPromise = (async () => {
      const db = (await opt.dbConnection) as IDBDatabase;

      if (!db || typeof db.transaction !== 'function') {
        throw new Error('Provided dbConnection is not a valid IDBDatabase');
      }

      return db;
    })();

    return globalDBPromise;
  }

  // If connection already created → reuse it
  if (globalDBPromise) return globalDBPromise;

  const DB_NAME = opt.dbName || INDEXED_DB_CONFIG.dbName;
  const VERSION = opt.version || INDEXED_DB_CONFIG.version;

  globalDBPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      Object.keys(INDEXED_DB_CONFIG.stores).forEach((s: string) => {
        //@ts-ignore
        const tempStoreName = INDEXED_DB_CONFIG.stores[s];
        if (!db.objectStoreNames.contains(tempStoreName)) {
          db.createObjectStore(tempStoreName, { autoIncrement: true });
        }
      });

      console.log('Database created / upgraded');
    };

    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        db.close();
        globalDBPromise = null;
      };
      db.onclose = () => {
        globalDBPromise = null;
      };
      resolve(db);
    };

    request.onerror = (event: any) => {
      reject(event?.target?.error?.message);
    };
  });

  return globalDBPromise;
}

// ---------------------------------------------------

function createIndexedDB(opt: TOptionsIdb) {
  const STORE_NAME = opt.storeName || '';
  const isDevelopmentMode = opt.isDevelopmentEnv;

  const storage: StateStorage & {
    init: () => Promise<IDBDatabase>;
    getAll: () => Promise<any>;
    clearAll: () => Promise<any>;
    setBulkItem: (val: any) => void;
  } = {
    init: async () => {
      const db = await getDB(opt);
      return db;
    },
    getAll: async () => {
      const db = await getDB(opt);

      return new Promise<Record<string, any>>((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);

        const cursorRequest = store.openCursor();
        const result: Record<string, any> = {};
        const pendingDecrypt: Promise<void>[] = [];

        cursorRequest.onsuccess = function (event) {
          const cursor = (event.target as any)
            ?.result as IDBCursorWithValue | null;

          if (cursor) {
            const key = String(cursor.key);
            const value = cursor.value;

            pendingDecrypt.push(
              (async () => {
                if (!isDevelopmentMode) {
                  const dec = await encrypted.decryptValue(value);
                  if (!isEmpty(dec) && typeof dec === 'string') {
                    result[key] = JSON.parse(dec);
                    return;
                  }
                }
                result[key] = value;
              })()
            );

            cursor.continue();
          } else {
            Promise.all(pendingDecrypt)
              .then(() => resolve(result))
              .catch(reject);
          }
        };

        cursorRequest.onerror = (event) => {
          reject('Error getAll: ' + (event.target as any)?.error?.message);
        };
      });
    },
    getItem: async (name: string): Promise<any> => {
      const db = await getDB(opt);

      return new Promise((resolve, reject) => {
        const tx = db.transaction([STORE_NAME], 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(name);

        req.onsuccess = async () => {
          const val = req.result;
          const isZustandPersistKey = name === STORE_NAME;

          if (!isDevelopmentMode && !isEmpty(val)) {
            const decrypted = await encrypted.decryptValue(val);
            if (!isEmpty(decrypted) && typeof decrypted === 'string') {
              const parsed = JSON.parse(decrypted);
              // When Zustand uses `createJSONStorage`, it expects `getItem` to return a JSON string.
              // Our non-dev encryption layer wraps values with JSON.stringify before encrypting.
              // For Zustand persisted entry, older data may have been stored as an object, so normalize.
              if (isZustandPersistKey && typeof parsed !== 'string') {
                resolve(JSON.stringify(parsed));
                return;
              }
              resolve(parsed);
              return;
            }
          }

          // Normalize Zustand persisted entry to a string to satisfy `createJSONStorage` contract.
          // Keep non-Zustand keys (e.g. 'userAccess') returning their original types for existing callers.
          if (isZustandPersistKey) {
            if (val == null) {
              resolve(null);
              return;
            }
            if (typeof val === 'string') {
              resolve(val);
              return;
            }
            try {
              resolve(JSON.stringify(val));
            } catch {
              resolve(String(val));
            }
            return;
          }

          resolve(val);
        };

        req.onerror = (event) => {
          reject(
            'IndexedDB get error:' + (event.target as any)?.error?.message
          );
        };
      });
    },
    setBulkItem: async (value: any): Promise<void> => {
      const db = await getDB(opt);

      try {
        const entries = Object.entries(value || {});
        const prepared = await Promise.all(
          entries.map(async ([key, rawValue]) => {
            if (isDevelopmentMode) {
              return [key, rawValue];
            }
            const enc = await encrypted.encryptValue(JSON.stringify(rawValue));
            return [key, enc];
          })
        );

        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction([STORE_NAME], 'readwrite');
          const store = tx.objectStore(STORE_NAME);

          prepared.forEach(([key, storeValue]) => {
            if (storeValue) {
              store.put(storeValue, key as IDBValidKey);
            }
          });

          tx.oncomplete = () => resolve();
          tx.onerror = (event) =>
            reject(
              'Error setBulkItem: ' + (event.target as any)?.error?.message
            );
        });
      } catch (err) {
        console.log('Error setBulkItem', err);
        throw err;
      }
    },
    setItem: async (name: string, value: any): Promise<void> => {
      const db = await getDB(opt);

      let storeValue = value;
      if (!isDevelopmentMode) {
        storeValue = await encrypted.encryptValue(JSON.stringify(value));
      }

      return new Promise((resolve, reject) => {
        const tx = db.transaction([STORE_NAME], 'readwrite');
        const store = tx.objectStore(STORE_NAME);

        if (storeValue && name) {
          store.put(storeValue, name);
        }

        tx.oncomplete = () => resolve();
        tx.onerror = (event) =>
          reject('Error setItem: ' + (event.target as any)?.error?.message);
      });
    },
    removeItem: async (name: string): Promise<void> => {
      console.log(name, 'has been deleted');
    },
    clearAll: async () => {
      const db = await getDB(opt);

      return new Promise((resolve, reject) => {
        const storeKeys = Object.keys(INDEXED_DB_CONFIG.stores);

        if (!storeKeys.length) {
          resolve('success clear');
          return;
        }

        let remaining = storeKeys.length;
        let rejected = false;

        storeKeys.forEach((s: string) => {
          //@ts-ignore
          const storeName = INDEXED_DB_CONFIG.stores[s];
          const tx = db.transaction([storeName], 'readwrite');
          const os = tx.objectStore(storeName);
          const req = os.clear();

          tx.oncomplete = () => {
            if (rejected) return;
            remaining -= 1;
            if (remaining === 0) resolve('success clear');
          };

          const onError = (event: any) => {
            if (rejected) return;
            rejected = true;
            reject('Error clearAll: ' + event?.target?.error?.message);
          };

          tx.onerror = onError;
          tx.onabort = onError;
          req.onerror = onError;
        });
      });
    },
  };

  return storage;
}

export const indexedDBStorage = (opt: TOptionsIdb) => createIndexedDB(opt);
